/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import ServicesService from '@/services/ServicesService';

export const BOT_HOSTS = {
  TLV: 'tlv',
  POLICE: 'police',
  LOLA: 'lola',
  SHARED: 'shared',
};

export const BOT_STATE = {
  Working: 'Working',
  Stopped: 'Stopped',
  Starting: 'Starting',
  WaitingForHuman: 'WaitingForHuman',
};

export const BOTS = [
  { key: 'Tlv', label: 'תל אביב', host: BOT_HOSTS.TLV, name: 'Tlv' },
  { key: 'TlvDocumentCompletion', label: 'תל אביב - השלמת מסמכים', host: BOT_HOSTS.SHARED, name: 'TlvDocumentCompletion' },
  { key: 'Police', label: 'משטרה', host: BOT_HOSTS.POLICE, name: 'Police' },
  { key: 'Lola', label: 'לולה-טק', host: BOT_HOSTS.LOLA, name: 'Lola' },
  { key: 'Jerusalem', label: 'ירושלים', host: BOT_HOSTS.SHARED, name: 'Jerusalem', excludeFromBulk: true },
  { key: 'Milgam', label: 'מילגם', host: BOT_HOSTS.SHARED, name: 'Milgam' },
  { key: 'MilgamAppeal', label: 'מילגם - ערעור', host: BOT_HOSTS.SHARED, name: 'MilgamAppeal' },
  {
    key: 'Automation',
    label: 'אוטומציה',
    host: BOT_HOSTS.SHARED,
    name: 'Automation',
    municipalities: ['ראשון לציון', 'באר שבע'],
    supportsRestart: true,
  },
  { key: 'Haifa', label: 'חיפה', host: BOT_HOSTS.SHARED, name: 'Haifa' },
];

const POLL_INTERVAL_MS = 30000;
const HTTP_NOT_FOUND = 404;

// Each bot health payload uses snake_case or camelCase depending on the host.
// Collapse both into one canonical shape.
function normalizePayload(raw) {
  if (!raw) return null;
  const waitingForHuman = raw.waiting_for_human ?? raw.waitingForHuman ?? false;
  const rawState = raw.state;
  let state;
  if (waitingForHuman) {
    state = BOT_STATE.WaitingForHuman;
  } else if (rawState === 'running' || rawState === 'polling' || rawState === 'idle') {
    state = BOT_STATE.Working;
  } else if (rawState === 'starting') {
    state = BOT_STATE.Starting;
  } else {
    state = BOT_STATE.Stopped;
  }
  return {
    state,
    currentReportNumber: raw.current_report_number ?? raw.currentReportNumber ?? null,
    transfersCompleted: raw.transfers_completed ?? raw.transfersCompleted ?? null,
    transfersFailed: raw.transfers_failed ?? raw.transfersFailed ?? null,
    startedAt: raw.started_at ?? raw.startedAt ?? null,
    lastActivity: raw.last_activity ?? raw.lastActivity ?? null,
    nextPollAt: raw.next_poll_at ?? raw.nextPollAt ?? null,
    waitingForHumanReason: raw.waiting_for_human_reason ?? raw.waitingForHumanReason ?? null,
  };
}

// Single-bot payloads sometimes wrap data under bots[Name]; fall through to the root.
function extractFromSingleHostPayload(data, name) {
  if (!data) return null;
  if (data.bots && data.bots[name]) return data.bots[name];
  return data;
}

// byKey: key -> normalized status object, or null when unknown/unreachable
// sharedServiceReachable: false when /api/v1/health failed entirely
// hostReachable: false when a single-bot host (tlv/police/lola) is unreachable
const state = () => ({
  byKey: {},
  sharedServiceReachable: true,
  hostReachable: {
    [BOT_HOSTS.TLV]: true,
    [BOT_HOSTS.POLICE]: true,
    [BOT_HOSTS.LOLA]: true,
  },
  lastPollAt: null,
  pollTimer: null,
});

const getters = {
  bots: (state) => BOTS.map((b) => ({
    ...b,
    status: state.byKey[b.key] || null,
    hostReachable: b.host === BOT_HOSTS.SHARED
      ? state.sharedServiceReachable
      : state.hostReachable[b.host],
  })),
  sharedServiceReachable: (state) => state.sharedServiceReachable,
  // Sidebar badge: fire only on WaitingForHuman or unreachable; silent on user-initiated Stop.
  hasAlert: (state) => {
    if (!state.sharedServiceReachable) return true;
    if (Object.values(state.hostReachable).some((r) => r === false)) return true;
    return Object.values(state.byKey).some((s) => s && s.state === BOT_STATE.WaitingForHuman);
  },
};

const mutations = {
  setBotStatus(state, { key, status }) {
    state.byKey = { ...state.byKey, [key]: status };
  },
  setHostReachable(state, { host, reachable }) {
    state.hostReachable = { ...state.hostReachable, [host]: reachable };
  },
  setSharedServiceReachable(state, reachable) {
    state.sharedServiceReachable = reachable;
  },
  setLastPollAt(state, ts) {
    state.lastPollAt = ts;
  },
  setPollTimer(state, timer) {
    state.pollTimer = timer;
  },
};

function applySingleHostResult({ commit }, host, name, result) {
  if (result.status === 'fulfilled') {
    commit('setHostReachable', { host, reachable: true });
    const raw = extractFromSingleHostPayload(result.value && result.value.data, name);
    BOTS.filter((b) => b.host === host).forEach((b) => {
      commit('setBotStatus', { key: b.key, status: normalizePayload(raw) });
    });
  } else {
    commit('setHostReachable', { host, reachable: false });
    BOTS.filter((b) => b.host === host).forEach((b) => {
      commit('setBotStatus', { key: b.key, status: null });
    });
  }
}

function applySharedResult({ commit }, result) {
  if (result.status === 'fulfilled') {
    commit('setSharedServiceReachable', true);
    const bots = (result.value && result.value.data) || {};
    BOTS.filter((b) => b.host === BOT_HOSTS.SHARED).forEach((b) => {
      const raw = bots[b.name] || null;
      commit('setBotStatus', { key: b.key, status: normalizePayload(raw) });
    });
  } else {
    commit('setSharedServiceReachable', false);
    BOTS.filter((b) => b.host === BOT_HOSTS.SHARED).forEach((b) => {
      commit('setBotStatus', { key: b.key, status: null });
    });
  }
}

const actions = {
  async pollAll({ commit }) {
    const results = await Promise.allSettled([
      ServicesService.tlvBotHealth(),
      ServicesService.policeBotHealth(),
      ServicesService.lolaBotHealth(),
      ServicesService.sharedBotsHealth(),
    ]);
    applySingleHostResult({ commit }, BOT_HOSTS.TLV, 'Tlv', results[0]);
    applySingleHostResult({ commit }, BOT_HOSTS.POLICE, 'Police', results[1]);
    applySingleHostResult({ commit }, BOT_HOSTS.LOLA, 'Lola', results[2]);
    applySharedResult({ commit }, results[3]);
    commit('setLastPollAt', Date.now());
  },
  startPolling({ state, commit, dispatch }) {
    if (state.pollTimer) return;
    dispatch('pollAll');
    const timer = setInterval(() => dispatch('pollAll'), POLL_INTERVAL_MS);
    commit('setPollTimer', timer);
  },
  stopPolling({ state, commit }) {
    if (state.pollTimer) {
      clearInterval(state.pollTimer);
      commit('setPollTimer', null);
    }
  },
  async startBot({ dispatch }, key) {
    const bot = BOTS.find((b) => b.key === key);
    if (!bot) return;
    if (bot.host === BOT_HOSTS.TLV) await ServicesService.startTlvBot();
    else if (bot.host === BOT_HOSTS.POLICE) await ServicesService.startPoliceBot();
    else if (bot.host === BOT_HOSTS.LOLA) await ServicesService.startLolaBot();
    else await ServicesService.startSharedBot(bot.name);
    dispatch('pollAll');
  },
  async stopBot({ dispatch }, key) {
    const bot = BOTS.find((b) => b.key === key);
    if (!bot) return;
    if (bot.host === BOT_HOSTS.TLV) await ServicesService.stopTlvBot();
    else if (bot.host === BOT_HOSTS.POLICE) await ServicesService.stopPoliceBot();
    else if (bot.host === BOT_HOSTS.LOLA) await ServicesService.stopLolaBot();
    else await ServicesService.stopSharedBot(bot.name);
    dispatch('pollAll');
  },
  // The bot service has no restart endpoint: stop, then start. A 404 on stop means it wasn't running.
  async restartBot({ dispatch }, key) {
    const bot = BOTS.find((b) => b.key === key);
    if (!bot || bot.host !== BOT_HOSTS.SHARED) return;
    try {
      await ServicesService.stopSharedBot(bot.name);
    } catch (e) {
      if (!e.response || e.response.status !== HTTP_NOT_FOUND) throw e;
    }
    await ServicesService.startSharedBot(bot.name);
    dispatch('pollAll');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
